import { useState, useEffect, useCallback, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
  Monitor,
  MapPin,
  Building2,
  CalendarDays,
  KeyRound,
  Timer,
  Camera,
  Search,
  ShieldCheck,
  AlertTriangle,
  Unlink,
  X,
  Delete,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import * as dbOps from '../db/kioskDbOperations';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr) {
  if (!dateStr) return 'Unknown';
  return new Date(dateStr).toLocaleDateString([], {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// AdminCodeDialog -- modal for changing the admin code
// ---------------------------------------------------------------------------

function AdminCodeDialog({ open, onOpenChange }) {
  const [step, setStep] = useState('enter'); // 'enter' | 'confirm' | 'success'
  const [code, setCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep('enter');
      setCode('');
      setConfirmCode('');
      setError('');
      setSaving(false);
    }
  }, [open]);

  const handleDigit = useCallback(
    (digit) => {
      setError('');
      if (step === 'enter') {
        setCode((prev) => (prev.length >= 6 ? prev : prev + digit));
      } else if (step === 'confirm') {
        setConfirmCode((prev) => (prev.length >= 6 ? prev : prev + digit));
      }
    },
    [step]
  );

  const handleDelete = useCallback(() => {
    setError('');
    if (step === 'enter') {
      setCode((prev) => prev.slice(0, -1));
    } else if (step === 'confirm') {
      setConfirmCode((prev) => prev.slice(0, -1));
    }
  }, [step]);

  const handleClear = useCallback(() => {
    setError('');
    if (step === 'enter') {
      setCode('');
    } else if (step === 'confirm') {
      setConfirmCode('');
    }
  }, [step]);

  const handleNext = useCallback(async () => {
    if (step === 'enter') {
      if (code.length < 4) {
        setError('Code must be at least 4 digits');
        return;
      }
      setStep('confirm');
    } else if (step === 'confirm') {
      if (confirmCode !== code) {
        setError('Codes do not match');
        setConfirmCode('');
        return;
      }
      setSaving(true);
      try {
        await dbOps.setConfig('admin_code', code);
        setStep('success');
      } catch {
        setError('Failed to save. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  }, [step, code, confirmCode]);

  // Auto-advance when 6 digits entered
  useEffect(() => {
    if (step === 'enter' && code.length === 6) {
      setStep('confirm');
    }
  }, [step, code]);

  useEffect(() => {
    if (step === 'confirm' && confirmCode.length === 6) {
      handleNext();
    }
  }, [step, confirmCode, handleNext]);

  const activeCode = step === 'enter' ? code : confirmCode;
  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', 'DEL'];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-700" />
            <h3 className="text-lg font-bold text-slate-800">
              {step === 'success' ? 'Code Updated' : 'Change Admin Code'}
            </h3>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success state */}
        {step === 'success' ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Admin code has been updated successfully.</p>
            <Button className="mt-4" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        ) : (
          <>
            {/* Instructions */}
            <p className="text-sm text-slate-500 mb-4">
              {step === 'enter'
                ? 'Enter a new 4-6 digit admin code'
                : 'Confirm the code by entering it again'}
            </p>

            {/* Code dots */}
            <div className="flex justify-center gap-2.5 mb-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3.5 h-3.5 rounded-full transition-colors ${
                    i < activeCode.length ? 'bg-slate-800' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Error */}
            {error && (
              <p className="text-center text-xs text-red-500 font-medium mb-3">{error}</p>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {digits.map((d) => {
                if (d === 'CLR') {
                  return (
                    <button
                      key={d}
                      onClick={handleClear}
                      className="h-12 rounded-lg bg-slate-100 text-slate-500 text-xs font-semibold active:bg-slate-200 transition-colors"
                    >
                      CLR
                    </button>
                  );
                }
                if (d === 'DEL') {
                  return (
                    <button
                      key={d}
                      onClick={handleDelete}
                      className="h-12 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center active:bg-slate-200 transition-colors"
                    >
                      <Delete className="w-4 h-4" />
                    </button>
                  );
                }
                return (
                  <button
                    key={d}
                    onClick={() => handleDigit(d)}
                    className="h-12 rounded-lg bg-slate-50 text-slate-800 text-xl font-semibold hover:bg-slate-100 active:bg-slate-200 transition-colors"
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            {/* Manual continue button for 4-5 digit codes */}
            {step === 'enter' && code.length >= 4 && code.length < 6 && (
              <Button className="w-full" onClick={handleNext}>
                Continue
              </Button>
            )}
            {step === 'confirm' && confirmCode.length >= 4 && confirmCode.length < 6 && (
              <Button className="w-full" onClick={handleNext} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm
              </Button>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KioskAdminSettings
// ---------------------------------------------------------------------------

export default function KioskAdminSettings({ deviceInfo, syncEngine, clearDevice }) {
  const [settings, setSettings] = useState({
    requirePin: true,
    allowNameSearch: true,
    requireSelfie: false,
    idleTimeout: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdminCodeDialog, setShowAdminCodeDialog] = useState(false);
  const saveTimeoutRef = useRef(null);

  // -----------------------------------------------------------------------
  // Load settings from deviceInfo
  // -----------------------------------------------------------------------
  useEffect(() => {
    async function loadSettings() {
      try {
        const info = await dbOps.getConfig('device_info');
        const s = info?.settings || deviceInfo?.settings || {};
        setSettings({
          requirePin: s.require_pin ?? s.requirePin ?? true,
          allowNameSearch: s.allow_name_search ?? s.allowNameSearch ?? true,
          requireSelfie: s.require_selfie ?? s.requireSelfie ?? false,
          idleTimeout: s.idle_timeout ?? s.idleTimeout ?? 30,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSettings();
  }, [deviceInfo]);

  // -----------------------------------------------------------------------
  // Auto-save settings when they change (debounced)
  // -----------------------------------------------------------------------
  const persistSettings = useCallback(
    async (newSettings) => {
      setSaving(true);
      try {
        const info = (await dbOps.getConfig('device_info')) || deviceInfo || {};
        const updatedInfo = {
          ...info,
          settings: {
            ...(info.settings || {}),
            require_pin: newSettings.requirePin,
            allow_name_search: newSettings.allowNameSearch,
            require_selfie: newSettings.requireSelfie,
            idle_timeout: newSettings.idleTimeout,
          },
        };
        await dbOps.setConfig('device_info', updatedInfo);
      } catch (err) {
        console.error('Failed to save settings:', err);
      } finally {
        setSaving(false);
      }
    },
    [deviceInfo]
  );

  const updateSetting = useCallback(
    (key, value) => {
      setSettings((prev) => {
        const next = { ...prev, [key]: value };

        // Debounce persistence
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
          persistSettings(next);
        }, 500);

        return next;
      });
    },
    [persistSettings]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // -----------------------------------------------------------------------
  // Idle timeout handler with clamping
  // -----------------------------------------------------------------------
  const handleIdleTimeoutChange = useCallback(
    (e) => {
      const raw = e.target.value;
      // Allow empty string while typing
      if (raw === '') {
        updateSetting('idleTimeout', '');
        return;
      }
      const val = parseInt(raw, 10);
      if (!isNaN(val)) {
        updateSetting('idleTimeout', Math.max(5, Math.min(300, val)));
      }
    },
    [updateSetting]
  );

  const handleIdleTimeoutBlur = useCallback(() => {
    setSettings((prev) => {
      const val = parseInt(prev.idleTimeout, 10);
      const clamped = isNaN(val) ? 30 : Math.max(5, Math.min(300, val));
      if (clamped !== prev.idleTimeout) {
        persistSettings({ ...prev, idleTimeout: clamped });
      }
      return { ...prev, idleTimeout: clamped };
    });
  }, [persistSettings]);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Device configuration and preferences</p>
        </div>
        {saving && (
          <span className="text-xs text-slate-400 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Saving...
          </span>
        )}
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Device Info                                                       */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Monitor className="w-4 h-4 text-slate-500" />
            Device Information
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pt-3">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <Monitor className="w-3 h-3" />
                Device Name
              </dt>
              <dd className="mt-0.5 text-sm text-slate-700 font-medium">
                {deviceInfo?.device_name || deviceInfo?.deviceName || 'Unnamed Device'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <MapPin className="w-3 h-3" />
                Site Location
              </dt>
              <dd className="mt-0.5 text-sm text-slate-700 font-medium">
                {deviceInfo?.location_name || deviceInfo?.locationName || 'Unknown'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <Building2 className="w-3 h-3" />
                Company
              </dt>
              <dd className="mt-0.5 text-sm text-slate-700 font-medium">
                {deviceInfo?.company_name || deviceInfo?.companyName || 'Unknown'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <CalendarDays className="w-3 h-3" />
                Paired Date
              </dt>
              <dd className="mt-0.5 text-sm text-slate-700 font-medium">
                {formatDate(deviceInfo?.paired_at || deviceInfo?.pairedAt)}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Login Methods                                                     */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" />
            Login Methods
          </CardTitle>
          <CardDescription>
            Configure how employees identify themselves at the kiosk
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pt-4 space-y-4">
          {/* Require PIN */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="setting-pin" className="text-sm font-medium text-slate-700">
                Require PIN
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Employees must enter their kiosk PIN to identify
              </p>
            </div>
            <Switch
              id="setting-pin"
              checked={settings.requirePin}
              onCheckedChange={(checked) => updateSetting('requirePin', checked)}
              disabled={loading}
            />
          </div>

          <Separator />

          {/* Allow Name Search */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="setting-search" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5 text-slate-400" />
                Allow Name Search
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Employees can find themselves by searching their name
              </p>
            </div>
            <Switch
              id="setting-search"
              checked={settings.allowNameSearch}
              onCheckedChange={(checked) => updateSetting('allowNameSearch', checked)}
              disabled={loading}
            />
          </div>

          <Separator />

          {/* Require Selfie */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="setting-selfie" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5 text-slate-400" />
                Require Selfie
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Capture a selfie photo when clocking in
              </p>
            </div>
            <Switch
              id="setting-selfie"
              checked={settings.requireSelfie}
              onCheckedChange={(checked) => updateSetting('requireSelfie', checked)}
              disabled={loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Idle Timeout                                                      */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Timer className="w-4 h-4 text-slate-500" />
            Idle Timeout
          </CardTitle>
          <CardDescription>
            Seconds of inactivity before the kiosk returns to the home screen (5-300)
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pt-4">
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={5}
              max={300}
              value={settings.idleTimeout}
              onChange={handleIdleTimeoutChange}
              onBlur={handleIdleTimeoutBlur}
              disabled={loading}
              className="w-24 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-colors disabled:opacity-50"
              aria-label="Idle timeout in seconds"
            />
            <span className="text-sm text-slate-500">seconds</span>
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Admin Code + Auto-Sync                                            */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-500" />
            Security & Sync
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pt-4 space-y-4">
          {/* Change Admin Code */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Admin Code</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Change the code required to access this admin panel
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setShowAdminCodeDialog(true)}
            >
              <KeyRound className="w-3.5 h-3.5" />
              Change
            </Button>
          </div>

          <Separator />

          {/* Auto-Sync */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="setting-autosync" className="text-sm font-medium text-slate-700">
                Auto-Sync
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Automatically synchronise events every 5 minutes when online
              </p>
            </div>
            <Switch
              id="setting-autosync"
              checked={syncEngine.autoSync}
              onCheckedChange={(checked) => syncEngine.toggleAutoSync(checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Danger Zone                                                       */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4 border-red-200">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that affect this device's configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
            <div>
              <p className="text-sm font-medium text-red-800">Unpair Device</p>
              <p className="text-xs text-red-600 mt-0.5">
                Removes all local data and disconnects this device from the server. This action
                cannot be undone.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-1.5 flex-none">
                  <Unlink className="w-3.5 h-3.5" />
                  Unpair Device
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Unpair this device?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove all locally stored data including employees, events, selfies,
                    and settings. Any unsynced events will be permanently lost. The device will need
                    to be re-paired to use the kiosk again.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={clearDevice}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Unpair Device
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Admin Code Dialog                                                 */}
      {/* ----------------------------------------------------------------- */}
      <AnimatePresence>
        {showAdminCodeDialog && (
          <AdminCodeDialog
            open={showAdminCodeDialog}
            onOpenChange={setShowAdminCodeDialog}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
